/**
 * Материалы монтажа под заголовком «Новый монтаж»: белая кнопка раскрывается
 * в карточку с двумя областями — видео рассказа и иллюстрации для врезок.
 */
import { useRef, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { LIMITS } from '@reels/shared/constants';
import { isDemo } from '../lib/api';
import type { ProjectAsset } from '../lib/types';
import { ACCEPT_ILLUSTRATION, ACCEPT_REACTION, formatBytes, formatDuration } from '../lib/video';
import { useScrollEdges } from '../lib/useScrollEdges';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import type { UploadItem } from '../state/store';
import { AssetRow, UploadRow, UploadThumbnail } from './Uploads';
import { t } from '../i18n';

const minutes = (seconds: number): number => Math.round(seconds / 60);

const illustrationsLabel = (count: number): string => t('components.materials.illustrations', { n: count });
const videosLabel = (count: number): string => t('components.materials.videos', { n: count });

const activeUpload = (item: UploadItem): boolean => ['uploading', 'checking', 'queued'].includes(item.status);

/** Что загружено и грузится сейчас: файлы этого захода и те, что пришли с черновиком. */
export function useMaterials(preparingDemo: boolean) {
  const { state } = useStore();
  const reactions = state.uploads.filter(item => item.slot === 'reaction');
  const illustrations = state.uploads.filter(item => item.slot === 'illustration');
  const uploadedIds = new Set(state.uploads.map(item => item.assetId).filter((id): id is string => id !== null));
  const existing = (state.project?.assets ?? []).filter(asset => !uploadedIds.has(asset.id));
  const existingIllustrations = existing.filter(asset => asset.kind !== 'reaction');
  const mainRows: Array<{ key: string; position: number; asset: ProjectAsset | null; item: UploadItem | null }> = [
    ...existing.filter(asset => asset.kind === 'reaction').map(asset => ({ key: asset.id, position: asset.position ?? 0, asset, item: null })),
    ...reactions.map(item => ({ key: item.id, position: item.position ?? 0, asset: null, item })),
  ].sort((a, b) => a.position - b.position);
  const reactionUploading = preparingDemo || reactions.some(activeUpload);
  const illustrationUploading = illustrations.some(activeUpload);
  const videosReady = mainRows.length > 0 && reactions.every(item => item.status === 'done')
    && mainRows.every(row => row.item || row.asset?.uploaded !== false);
  const illustrationsReady = illustrations.every(item => item.status === 'done') && existingIllustrations.every(asset => asset.uploaded !== false);
  const totalBytes = mainRows.reduce((n, row) => n + (row.item?.bytes ?? row.asset?.bytes ?? 0), 0);
  const uploadedBytes = mainRows.reduce((n, row) => n + (row.item?.uploadedBytes ?? row.asset?.bytes ?? 0), 0);
  const illustrationBytes = illustrations.reduce((n, item) => n + item.bytes, 0);
  const illustrationUploaded = illustrations.reduce((n, item) => n + item.uploadedBytes, 0);
  return {
    reactions,
    illustrations,
    existingIllustrations,
    mainRows,
    illustrationCount: illustrations.length + existingIllustrations.length,
    reactionUploading,
    illustrationUploading,
    uploading: reactionUploading || illustrationUploading,
    videosReady,
    ready: videosReady && illustrationsReady,
    totalBytes,
    totalDuration: mainRows.reduce((n, row) => n + (row.item?.durationS ?? row.asset?.durationS ?? 0), 0),
    videoPercent: videosReady ? 100 : Math.round(totalBytes ? 100 * uploadedBytes / totalBytes : 0),
    illustrationPercent: Math.round(illustrationBytes ? 100 * illustrationUploaded / illustrationBytes : 0),
    illustrationsReady,
  };
}

export type MaterialsState = ReturnType<typeof useMaterials>;

function PlusIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Вторая строка кнопки: что уже загружено или сколько осталось. Без материалов её нет. */
function summaryLine(materials: MaterialsState): ReactElement | null {
  const videos = materials.mainRows.length;
  const pictures = materials.illustrationCount;
  if (materials.reactionUploading && videos) return <>{t('components.materials.uploadingVideos', { videos: videosLabel(videos), percent: materials.videoPercent })}</>;
  if (materials.illustrationUploading) return <>{t('components.materials.uploadingIllustrations', { items: illustrationsLabel(materials.illustrations.length), percent: materials.illustrationPercent })}</>;
  if (!videos && !pictures) return null;
  const check = <span className="ok">✓</span>;
  return <>
    {videos ? <span>{videosLabel(videos)} {materials.videosReady ? check : t('components.materials.checkUpload')}</span> : null}
    {pictures ? <span>{illustrationsLabel(pictures)} {materials.illustrationsReady ? check : t('components.materials.checkUpload')}</span> : null}
  </>;
}

export function Materials({ materials, open, onToggle, disabled, onDemo }: {
  materials: MaterialsState;
  open: boolean;
  onToggle: () => void;
  /** Монтаж уже запущен: файлы не меняются. */
  disabled: boolean;
  /** В демо видео не выбирают, а подкладывают готовые примеры. */
  onDemo: () => void;
}): ReactElement {
  const { state } = useStore();
  const actions = useActions();
  const reactionInput = useRef<HTMLInputElement>(null);
  const illustrationInput = useRef<HTMLInputElement>(null);
  const [showRequirements, setShowRequirements] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const { mainRows, reactions, illustrations, existingIllustrations, illustrationCount } = materials;
  const thumbs = useScrollEdges<HTMLDivElement>([mainRows.length]);
  const summary = summaryLine(materials);
  const failed = reactions.some(item => item.status === 'error' || item.status === 'canceled');

  const pick = (event: ChangeEvent<HTMLInputElement>, slot: 'reaction' | 'illustration'): void => {
    const files = event.target.files;
    if (files && files.length > 0) void actions.addFiles(files, slot);
    // Сбрасываем значение, иначе повторный выбор того же файла не сработает.
    event.target.value = '';
  };
  const pickVideo = (): void => {
    if (isDemo()) onDemo();
    else reactionInput.current?.click();
  };

  return (
    <section className="materials" aria-label={t('components.materials.aria')}>
      <button type="button" className="materials-toggle" aria-expanded={open} aria-controls="materials-body" onClick={onToggle}>
        <span className="col">
          <span className="materials-title">{t('components.materials.title')}</span>
          {summary ? <span className="materials-summary">{summary}</span> : null}
        </span>
        <span className="materials-chevron" aria-hidden="true" />
      </button>
      {open ? <fieldset id="materials-body" className="materials-body" disabled={disabled}>
        <div className="material-area">
          <div className="material-area-head">
            <span className="col grow">
              <span className="material-area-title">{mainRows.length ? t('components.materials.yourVideos') : t('components.materials.videosTitle')}</span>
              <span className="material-area-hint">{t('components.materials.videosHint')}</span>
            </span>
            {mainRows.length < LIMITS.sources.maxCount ? <button type="button" className="plus" aria-label={t('components.materials.addVideo')}
              disabled={disabled || state.busy || materials.reactionUploading} onClick={pickVideo}><PlusIcon /></button> : null}
          </div>
          {mainRows.length > 0 ? <>
            <div ref={thumbs.ref} className={'source-thumbnails ' + thumbs.fadeClass} aria-label={t('components.materials.thumbsAria')} onScroll={thumbs.measure}>
              {mainRows.map(row => <UploadThumbnail key={row.key}
                src={row.item?.thumbUrl ?? row.asset?.thumbUrl} video={!!row.item}
                done={row.item ? row.item.status === 'done' : row.asset?.uploaded !== false} />)}
            </div>
            <span className="material-files">
              {materials.videosReady
                ? t('components.materials.uploadedSummary', { videos: videosLabel(mainRows.length), duration: formatDuration(materials.totalDuration), size: formatBytes(materials.totalBytes) })
                : materials.reactionUploading ? t('components.materials.uploadingSummary', { videos: videosLabel(mainRows.length), percent: materials.videoPercent }) : t('components.materials.checkSummary', { videos: videosLabel(mainRows.length) })}
            </span>
            {materials.reactionUploading ? <>
              <div className="bar"><span style={{ width: `${materials.videoPercent}%` }} /></div>
              <div className="upload-notice" role="status">{t('components.materials.notice')}</div>
            </> : <>
              <button type="button" className="material-link" aria-expanded={showFiles || failed} aria-controls="material-files"
                onClick={() => setShowFiles(value => !value)}>
                {t('components.materials.filesOrder')} <span className="chevron" />
              </button>
              {showFiles || failed ? <div id="material-files" className="material-file-list">
                <p>{t('components.materials.orderHint')}</p>
                {mainRows.map((row, index) => {
                  const assetId = row.item?.assetId ?? row.asset?.id;
                  const locked = disabled || !materials.ready || state.busy;
                  return <div key={row.key} className="source-item">
                    <div className="row between source-order">
                      <span>{t('components.materials.file', { n: index + 1 })}</span>
                      <div className="row">
                        <button type="button" className="btn ghost small" aria-label={t('components.materials.moveUp', { n: index + 1 })} disabled={locked || index === 0} onClick={() => assetId && void actions.moveSource(assetId, -1)}>↑</button>
                        <button type="button" className="btn ghost small" aria-label={t('components.materials.moveDown', { n: index + 1 })} disabled={locked || index === mainRows.length - 1} onClick={() => assetId && void actions.moveSource(assetId, 1)}>↓</button>
                      </div>
                    </div>
                    {row.asset ? <AssetRow asset={row.asset} onRemove={() => void actions.removeAsset(row.asset!.id)} />
                      : row.item ? <UploadRow item={row.item} onRemove={() => void actions.removeUpload(row.item!.id)} onRetry={() => void actions.retryUpload(row.item!.id)} /> : null}
                  </div>;
                })}
              </div> : null}
            </>}
          </> : null}
          <button type="button" className="material-link" aria-expanded={showRequirements} aria-controls="video-requirements"
            onClick={() => setShowRequirements(value => !value)}>
            {t('components.materials.requirements')} <span className="chevron" />
          </button>
          {showRequirements ? <div id="video-requirements" className="material-requirements">
            {t('components.materials.limitSources', { count: LIMITS.sources.maxCount, minutes: minutes(LIMITS.sources.maxDurationS), size: formatBytes(LIMITS.sources.maxBytes) })}{' '}
            {t('components.materials.limitFile', { size: formatBytes(LIMITS.reaction.maxBytes) })}{' '}
            {t('components.materials.limitIllustrations', { count: LIMITS.maxIllustrations, minutes: minutes(LIMITS.illustrationVideo.maxDurationS), videoSize: formatBytes(LIMITS.illustrationVideo.maxBytes), photoSize: formatBytes(LIMITS.photo.maxBytes) })}
          </div> : null}
        </div>
        <div className="material-area">
          <div className="material-area-head">
            <span className="col grow">
              <span className="material-area-title">{t('components.materials.illustrationsTitle', { count: illustrationCount, max: LIMITS.maxIllustrations })}</span>
              <span className="material-area-hint">{illustrationCount < LIMITS.maxIllustrations ? t('components.materials.illustrationsHint') : t('components.materials.illustrationsMax')}</span>
            </span>
            {illustrationCount < LIMITS.maxIllustrations ? <button type="button" className="plus" aria-label={t('components.materials.addIllustrations')}
              disabled={disabled || state.busy || materials.illustrationUploading} onClick={() => illustrationInput.current?.click()}><PlusIcon /></button> : null}
          </div>
          {existingIllustrations.map(asset => <AssetRow key={asset.id} asset={asset} onRemove={() => void actions.removeAsset(asset.id)} />)}
          {illustrations.map(item => <UploadRow key={item.id} item={item}
            onRemove={() => void actions.removeUpload(item.id)} onRetry={() => void actions.retryUpload(item.id)} />)}
        </div>
      </fieldset> : null}
      <input ref={reactionInput} className="hidden-input" type="file" multiple accept={ACCEPT_REACTION} onChange={event => pick(event, 'reaction')} />
      <input ref={illustrationInput} className="hidden-input" type="file" multiple accept={ACCEPT_ILLUSTRATION} onChange={event => pick(event, 'illustration')} />
    </section>
  );
}
